import { Token } from "./BuildTokens";

export type MeasuredToken = {
  token: Token;
  rect: DOMRect;
};

function splitTokensByBr(tokens: Token[]): Token[][] {
  const result: Token[][] = [];
  let chunk: Token[] = [];
  for (const token of tokens) {
    if (token.type === "br") {
      if (chunk.length) result.push(chunk);
      result.push([token]);
      chunk = [];
    } else {
      chunk.push(token);
    }
  }
  if (chunk.length) result.push(chunk);
  return result;
}

/**
 * Measures the layout of tokens within a given container element.
 * This function calculates the bounding rectangles for each token
 * by rendering them temporarily in a hidden wrapper element and
 * measuring their dimensions.
 *
 * @param tokens - An array of tokens to be measured. Each token can be of type "text", "element", or "other".
 *   - "text": Represents a text token with content to be measured.
 *   - "element": Represents an HTML element token to be measured.
 *   - "other": Tokens of this type are ignored.
 * @param container - The container HTMLElement within which the tokens are measured.
 *   The width of this container is used to constrain the layout.
 * @returns An array of measured tokens, where each measured token contains:
 *   - `token`: The original token with additional information for "text" tokens.
 *   - `rect`: The DOMRect representing the bounding rectangle of the token.
 */
export function LayoutMeasurer(tokens: Token[], container: HTMLElement): MeasuredToken[] {
  const wrapper = document.createElement("div");
  const computed = window.getComputedStyle(container);
  wrapper.style.position = "absolute";
  wrapper.style.visibility = "hidden";
  wrapper.style.pointerEvents = "none";
  wrapper.style.width = container.clientWidth + "px";
  wrapper.style.padding = computed.padding;
  wrapper.style.font = computed.font;
  wrapper.style.letterSpacing = computed.letterSpacing;
  wrapper.style.lineHeight = computed.lineHeight;
  wrapper.style.fontVariant = computed.fontVariant;
  wrapper.style.fontStretch = computed.fontStretch;

  container.appendChild(wrapper);

  const measured: MeasuredToken[] = [];
  const chunks = splitTokensByBr(tokens);

  let isAfterElement = false;

  for (const chunk of chunks) {
    if (chunk.length === 1 && chunk[0].type === "br") {
      measured.push({
        token: chunk[0],
        rect: new DOMRect(0, 0, 0, 0),
      });
      continue;
    }
    if (chunk.length === 0) continue;

    const wrapper = document.createElement("div");
    wrapper.style.position = "absolute";
    wrapper.style.visibility = "hidden";
    wrapper.style.pointerEvents = "none";
    wrapper.style.width = container.clientWidth + "px";
    wrapper.style.padding = computed.padding;
    wrapper.style.font = computed.font;
    wrapper.style.letterSpacing = computed.letterSpacing;
    wrapper.style.lineHeight = computed.lineHeight;
    wrapper.style.fontVariant = computed.fontVariant;
    wrapper.style.fontStretch = computed.fontStretch;
    container.appendChild(wrapper);

    chunk.forEach((token, idx) => {
      let node: Node;
      let span;
      let rect;
      switch (token.type) {
        case "text":
          let words = token.content
            .trim()
            .split(/\s+/)
            .filter((word) => word.length > 0);

          words.forEach((word, wordInd) => {
            node = document.createTextNode(word);

            span = document.createElement("span");
            span.style.display = "inline-block";
            span.appendChild(node);
            const spaceText = document.createTextNode("\u00a0");
            span.appendChild(spaceText);

            wrapper.appendChild(span);
            rect = span.getBoundingClientRect();
            let measuredText: MeasuredToken = {
              token: {
                type: "text",
                id: "",
                node: node as Text,
                content: word,
              },
              rect,
            };
            if (isAfterElement) {
              isAfterElement = false;
              measuredText.token.meta = {
                ...(measuredText.token.meta || {}),
                isAfterElement: true,
              };
            }
            measured.push(measuredText);
          });

          break;
        case "element":
          node = token.node.cloneNode(true);
          span = document.createElement("span");
          span.style.display = "inline-block";
          span.appendChild(node);
          wrapper.appendChild(span);
          rect = span.getBoundingClientRect();
          measured.push({ token, rect });

          const prev = measured[measured.length - 2];
          if (prev?.token.type === "text") {
            prev.token.meta = {
              ...(prev.token.meta || {}),
              isBeforeElement: true,
            };
          }
          isAfterElement = true;
          break;
        case "space":
        case "other":
          break;
      }
    });

    container.removeChild(wrapper);
  }
  return measured;
}
